import type { LayoutMode } from '../index';
import { TopBannerSkeleton } from './TopBannerSkeleton';

type SpeedPageSkeletonProps = {
  showToc: boolean;
  showVerticalAlerts: boolean;
  showVerticalTrackers: boolean;
  alertsLayout: LayoutMode;
  relatedLinksLayout: LayoutMode;
};

const TOC_KEYS = [
  'sp-toc-1',
  'sp-toc-2',
  'sp-toc-3',
  'sp-toc-4',
  'sp-toc-5',
  'sp-toc-6',
  'sp-toc-7',
  'sp-toc-8',
];

const TOPIC_KEYS = [
  'sp-topic-1',
  'sp-topic-2',
  'sp-topic-3',
  'sp-topic-4',
  'sp-topic-5',
  'sp-topic-6',
];

const TopicRowsSkeleton = () => (
  <>
    {TOPIC_KEYS.map((key) => (
      <div className="mb-4" key={key}>
        <span
          className="placeholder rounded col-5 d-block mb-2"
          style={{ height: '1.1rem' }}
        />
        <span className="placeholder rounded col-11 d-block mb-2" />
        <span className="placeholder rounded col-9 d-block mb-2" />
        <span
          className="placeholder rounded col-7 d-block"
          style={{ height: '0.75rem' }}
        />
      </div>
    ))}
  </>
);

/**
 * Layout-accurate Speed Read skeleton — matches loaded V3 shell
 * (optional top banner + `.v3-report-content-card` with TOC + topic rows).
 */
export const SpeedPageSkeleton = ({
  showToc,
  showVerticalAlerts,
  showVerticalTrackers,
  alertsLayout,
  relatedLinksLayout,
}: SpeedPageSkeletonProps) => {
  const isAlertsVertical = alertsLayout === 'vertical' && showVerticalAlerts;
  const isTrackersVertical =
    relatedLinksLayout === 'vertical' && showVerticalTrackers;
  const showVerticalContainer = isAlertsVertical || isTrackersVertical;
  const showBannerSkeleton = showToc || showVerticalContainer;

  return (
    <div className="container-fluid">
      <div className="ao_mainContainer">
        {showBannerSkeleton && <TopBannerSkeleton />}

        {showToc || showVerticalContainer ? (
          <div className="row my-3">
            {showVerticalContainer && (
              <div className="float-end order-sm-1 order-md-3 ao_rightContainer mt-3 mt-sm-0">
                <div className="placeholder-glow" aria-hidden="true">
                  {isAlertsVertical && (
                    <div className="v3-report-content-card mb-3">
                      <span
                        className="placeholder rounded col-8 d-block mb-2"
                        style={{ height: '0.9rem' }}
                      />
                      <span className="placeholder rounded col-12 d-block mb-2" />
                      <span className="placeholder rounded col-10 d-block" />
                    </div>
                  )}
                  {isTrackersVertical && (
                    <div className="v3-report-content-card mb-3">
                      <span
                        className="placeholder rounded col-8 d-block mb-2"
                        style={{ height: '0.9rem' }}
                      />
                      <span className="placeholder rounded col-12 d-block mb-2" />
                      <span className="placeholder rounded col-9 d-block" />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="col order-md-2">
              <div
                className="v3-report-content-card placeholder-glow"
                aria-hidden="true"
              >
                <div className="row">
                  {showToc && (
                    <div className="col-md-3 d-none d-md-block order-md-1">
                      {TOC_KEYS.map((key) => (
                        <span
                          key={key}
                          className="placeholder rounded col-10 d-block mb-2"
                          style={{ height: '0.85rem' }}
                        />
                      ))}
                    </div>
                  )}
                  <div className="col order-md-2">
                    <TopicRowsSkeleton />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="my-3">
            <div
              className="v3-report-content-card placeholder-glow"
              aria-hidden="true"
            >
              <TopBannerSkeleton />
              <TopicRowsSkeleton />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** Conservative layout for Suspense / hop fallbacks before page layout is known. */
export const SpeedPageFallback = () => (
  <SpeedPageSkeleton
    showToc
    showVerticalAlerts={false}
    showVerticalTrackers={false}
    alertsLayout={null}
    relatedLinksLayout={null}
  />
);
